import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Loader2, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import BackButton from "components/ui/back-button";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Toaster } from "components/ui/sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import {
  ProviderSchedulingPatch,
  getCalendarConnection,
  getProviders,
  httpStatus,
  listConnectionCalendars,
  updateProviderScheduling,
} from "./calendarApi";
import CalendarConnectionSection, { CalendarsState } from "./CalendarConnectionSection";
import { INBOUND_CALLS_PATH } from "./paths";
import { CalendarConnection, ProviderSchedulingRow } from "./types";
import WorkingHoursDialog, { workingHoursLabel } from "./WorkingHoursDialog";

export { PROVIDER_SCHEDULING_PATH } from "./paths";

// Radix Select refuses value="", so "Not assigned" needs its own value.
const NO_CALENDAR = "__none__";

/**
 * Which clinicians the assistant may book and which calendar each is booked
 * into. Every change saves on its own, one field per PUT, so a failed save
 * leaves the other settings untouched.
 */
function ProviderSchedulingPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ProviderSchedulingRow[] | undefined>(undefined);
  const [connection, setConnection] = useState<CalendarConnection | null | undefined>(
    undefined
  );
  const [loadFailed, setLoadFailed] = useState(false);
  const [calendars, setCalendars] = useState<CalendarsState>({ kind: "loading" });
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [hoursProviderId, setHoursProviderId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadFailed(false);
    const [providersResult, connectionResult, calendarsResult] = await Promise.allSettled([
      getProviders(),
      getCalendarConnection(),
      listConnectionCalendars(),
    ]);
    if (providersResult.status === "fulfilled") {
      setRows(providersResult.value);
    } else {
      setRows([]);
      toast.error("Could not load providers.");
    }
    if (connectionResult.status === "fulfilled") {
      setConnection(connectionResult.value);
    } else if (httpStatus(connectionResult.reason) === 404) {
      setConnection(null);
    } else {
      setLoadFailed(true);
      setConnection(undefined);
    }
    if (calendarsResult.status === "fulfilled") {
      setCalendars({ kind: "ok", calendars: calendarsResult.value });
    } else {
      const status = httpStatus(calendarsResult.reason);
      setCalendars({
        kind: status === 404 ? "none" : status === 409 ? "reconnect" : "error",
      });
    }
  }, []);

  const loadCalendars = useCallback(async () => {
    setCalendars({ kind: "loading" });
    try {
      setCalendars({ kind: "ok", calendars: await listConnectionCalendars() });
    } catch (error) {
      const status = httpStatus(error);
      setCalendars({
        kind: status === 404 ? "none" : status === 409 ? "reconnect" : "error",
      });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (providerId: string, patch: ProviderSchedulingPatch): Promise<boolean> => {
    setSavingIds((ids) => new Set(ids).add(providerId));
    try {
      const scheduling = await updateProviderScheduling(providerId, patch);
      // The server keys the profile by NPI, so duplicate roster rows for one clinician share the result.
      const npi = rows?.find((row) => row.provider.id === providerId)?.provider.npi;
      setRows((current) =>
        current?.map((row) =>
          row.provider.id === providerId || (npi && row.provider.npi === npi)
            ? { ...row, scheduling }
            : row
        )
      );
      return true;
    } catch (error) {
      toast.error(
        httpStatus(error) === 400 && patch.google_calendar_id !== undefined
          ? "That calendar is no longer in the connected account. Pick another."
          : "Could not save. Try again."
      );
      return false;
    } finally {
      setSavingIds((ids) => {
        const next = new Set(ids);
        next.delete(providerId);
        return next;
      });
    }
  };

  const calendarList = useMemo(
    () => (calendars.kind === "ok" ? calendars.calendars : []),
    [calendars]
  );
  const calendarNameById = useMemo(
    () => new Map(calendarList.map((calendar) => [calendar.id, calendar.summary])),
    [calendarList]
  );

  const shared = useMemo(() => {
    const byCalendar = new Map<string, string[]>();
    for (const row of rows ?? []) {
      const id = row.scheduling.google_calendar_id;
      if (!id) continue;
      byCalendar.set(id, [...(byCalendar.get(id) ?? []), row.provider.name]);
    }
    return [...byCalendar.entries()].filter(([, names]) => names.length > 1);
  }, [rows]);

  const bookable = (rows ?? []).filter((row) => row.scheduling.is_active).length;
  const assigned = (rows ?? []).filter((row) => row.scheduling.google_calendar_id).length;
  const hoursRow = rows?.find((row) => row.provider.id === hoursProviderId);
  const needsReconnect =
    connection != null &&
    (connection.status === "PENDING" ||
      connection.status === "ERROR" ||
      calendars.kind === "reconnect");

  return (
    <div className="w-full bg-background">
      <div className="border-b-2 border-gray-100 px-4 py-2 sticky top-0 bg-white z-10">
        <BackButton onClick={() => navigate(INBOUND_CALLS_PATH)} />
      </div>

      <div className="p-4 space-y-4 max-w-5xl">
        <div>
          <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-gray-500" />
            Provider scheduling
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Connect a calendar, then choose which clinicians the assistant may
            book, which calendar each is booked into, and when. A provider
            without a calendar is never booked.
          </p>
        </div>

        <CalendarConnectionSection
          connection={connection}
          calendars={calendars}
          loadFailed={loadFailed}
          needsReconnect={needsReconnect}
          onRetry={load}
          onRefreshCalendars={loadCalendars}
          onDisconnected={() => {
            setConnection((current) =>
              current ? { ...current, status: "DISCONNECTED" } : null
            );
            setCalendars({ kind: "none" });
          }}
        />

        {rows === undefined ? (
          <p className="text-xs text-gray-500 flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading providers…
          </p>
        ) : rows.length === 0 ? (
          <p className="text-xs text-gray-500" data-testid="providers-empty">
            No providers on the roster yet.
          </p>
        ) : (
          <>
            <p className="text-xs text-gray-500" data-testid="providers-summary">
              {bookable} of {rows.length} bookable, {assigned} with a calendar.
            </p>
            <div className="rounded-md border border-border bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Provider</TableHead>
                    <TableHead className="text-xs text-center">Bookable</TableHead>
                    <TableHead className="text-xs text-center">New patients</TableHead>
                    <TableHead className="text-xs">Calendar</TableHead>
                    <TableHead className="text-xs">Hours</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(({ provider, scheduling }) => {
                    const saving = savingIds.has(provider.id);
                    const calendarId = scheduling.google_calendar_id;
                    const stale = !!calendarId && calendarList.length > 0 && !calendarNameById.has(calendarId);
                    return (
                      <TableRow key={provider.id} data-testid={`provider-row-${provider.id}`}>
                        <TableCell className="text-xs">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-800">{provider.name}</span>
                            {saving && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
                          </div>
                          <div className="text-gray-500">NPI {provider.npi}</div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox
                            checked={scheduling.is_active}
                            disabled={saving}
                            aria-label={`${provider.name}: bookable`}
                            data-testid={`active-${provider.id}`}
                            onCheckedChange={(checked) =>
                              save(provider.id, { is_active: checked === true })
                            }
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox
                            checked={scheduling.accepting_new_patients}
                            disabled={saving}
                            aria-label={`${provider.name}: accepting new patients`}
                            data-testid={`new-patients-${provider.id}`}
                            onCheckedChange={(checked) =>
                              save(provider.id, { accepting_new_patients: checked === true })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 min-w-0">
                            <Select
                              value={stale ? undefined : calendarId ?? NO_CALENDAR}
                              disabled={saving || calendars.kind !== "ok"}
                              onValueChange={(value) =>
                                save(provider.id, {
                                  google_calendar_id: value === NO_CALENDAR ? "" : value,
                                })
                              }
                            >
                              <SelectTrigger
                                className="h-8 text-xs w-56 max-w-full min-w-0 overflow-hidden [&>span]:min-w-0 [&>span]:flex-1 [&>span]:truncate"
                                aria-label={`${provider.name}: calendar`}
                                data-testid={`calendar-${provider.id}`}
                              >
                                <SelectValue placeholder={stale ? "Reselect a calendar" : "Not assigned"} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={NO_CALENDAR} className="text-xs">
                                  Not assigned
                                </SelectItem>
                                {calendarList.map((calendar) => (
                                  <SelectItem key={calendar.id} value={calendar.id} className="text-xs">
                                    {calendar.summary}
                                    {calendar.primary ? " (primary)" : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {stale && (
                              <Badge
                                variant="outline"
                                className="text-[10px] bg-amber-50 text-amber-800 border-amber-200"
                                data-testid={`stale-${provider.id}`}
                              >
                                Not in connected account
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs max-w-64 justify-start"
                            disabled={saving}
                            title={workingHoursLabel(scheduling.working_hours) ?? undefined}
                            aria-label={`${provider.name}: working hours`}
                            data-testid={`hours-${provider.id}`}
                            onClick={() => setHoursProviderId(provider.id)}
                          >
                            <Clock className="h-3.5 w-3.5 mr-1.5 shrink-0 text-gray-500" />
                            <span className="truncate">
                              {workingHoursLabel(scheduling.working_hours) ?? "Not set"}
                            </span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {hoursRow && (
              <WorkingHoursDialog
                // Keyed so switching providers starts from that provider's hours, not a stale draft.
                key={hoursRow.provider.id}
                providerName={hoursRow.provider.name}
                hours={hoursRow.scheduling.working_hours}
                saving={savingIds.has(hoursRow.provider.id)}
                onSave={(working_hours) => save(hoursRow.provider.id, { working_hours })}
                onClose={() => setHoursProviderId(null)}
              />
            )}
            {shared.length > 0 && (
              <p className="text-xs text-amber-800" data-testid="shared-calendar-note">
                {shared
                  .map(
                    ([id, names]) =>
                      `${names.join(" and ")} share "${calendarNameById.get(id) ?? id}".`
                  )
                  .join(" ")}{" "}
                Busy time on a shared calendar blocks every provider on it.
              </p>
            )}
          </>
        )}
      </div>
      <Toaster richColors position="top-center" />
    </div>
  );
}

export default ProviderSchedulingPage;
